/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  RACTextbox,
  RACSelect,
  RACButton,
  Box,
  RACDatePicker,
  Card,
  CardContent,
  CircularProgress,
  RACMUIDatePicker,
} from '@rentacenter/racstrap';
import {
  CustomerRecordDispatchStateContext,
  CustomerRecordStateContext,
} from '../../context/CustomerRecordCheck/CustomerRecordProvider';
import { globalStyles } from '../../jsStyles/globalStyles';
import { GetState, postSearch } from '../../api/user';
import moment from 'moment';
import CustomerSearchResultGrid from './CRCResultRecord';
import {
  INITIAL_OFFSET,
  RECORD_CHECK_LIST_API_LIMIT,
} from '../../constants/constants';
import { getSelectedStore } from '../../utils/utils';
import { useTranslation } from 'react-i18next';
import { useStoreDetails } from '../../context/Store/StoreProvider';

export default function CRCSearchForm() {
  const classes = globalStyles();
  const { t } = useTranslation();
  const {
    customerBasicInformation,
    isSearchButtonEnabled,
    errorMessage,
    stateOptions,
    queuedCustomers,
    selectedCustomerQueue,
  } = useContext(CustomerRecordStateContext);
  const {
    setCustomerBasicInformation,
    setStateOptions,
    setSearchResults,
    setErrorMessage,
    resetForm,
    setSearchListLoading,
    setOffsetValue,
    setMainSearch,
  } = useContext(CustomerRecordDispatchStateContext);

  const [screenLoader, setScreenLoader] = useState<boolean>(false);
  const [dropDownLoading, setDropDownLoading] = useState<boolean>(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const { DateTimeLocalization } = useStoreDetails();

  useEffect(() => {
    const fetchStateOptions = async () => {
      try {
        setDropDownLoading(true);
        const response = await GetState();
        const states = response.map((state: any) => ({
          value: state.stateAbbreviation,
          label: state.stateProvinceName,
        }));

        setStateOptions([{ label: 'Select', value: '0' }, ...states]);
      } catch (error) {
        console.error('Failed to fetch states', error);
        setErrorMessage('Failed to load states');
        setStateOptions([]);
      } finally {
        setDropDownLoading(false);
      }
    };

    fetchStateOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (e: any) => {
    const InputTxt = e;
    const name = 'dateOfBirth';
    const format = moment(InputTxt).format('MM/DD/YYYY');
    const temp = InputTxt.replace(format);
    setCustomerBasicInformation({
      ...customerBasicInformation,
      [name]: temp,
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<{
      name?: string;
      value: unknown;
    }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      if (name === 'ssn1' || name === 'ssn2' || name === 'ssn3') {
        setCustomerBasicInformation({
          [name]: value,
        });
      } else {
        setCustomerBasicInformation({
          ...customerBasicInformation,
          [name]: value,
        });
      }
    }
  };

  const ssn1Handler = (e: any) => {
    const ssn2El: any = document.getElementById('a11y_ssntwo');
    const ssn1El: any = document.getElementById('a11y_ssnone');
    if (e.target.value.length === 3 && ssn1El == document.activeElement) {
      ssn2El.focus();
    }
    handleInputChange(e);
  };

  const ssn2Handler = (e: any) => {
    const ssn3El: any = document.getElementById('a11y_ssnthree');
    const ssn1El: any = document.getElementById('a11y_ssnone');
    const ssn2El: any = document.getElementById('a11y_ssntwo');
    if (e.target.value.length === 2 && ssn2El == document.activeElement) {
      ssn3El.focus();
    }
    if (
      e.target.value.length === 0 &&
      customerBasicInformation.ssn1?.length > 0 &&
      ssn2El == document.activeElement
    ) {
      ssn1El.focus();
    }
    handleInputChange(e);
  };

  const ssn3Handler = (e: any) => {
    const ssn3El: any = document.getElementById('a11y_ssnthree');
    const ssn2El: any = document.getElementById('a11y_ssntwo');
    if (
      e.target.value.length === 0 &&
      customerBasicInformation.ssn2.length > 0 &&
      ssn3El == document.activeElement
    ) {
      ssn2El.focus();
    }
    handleInputChange(e);
  };

  const handleSearch = async () => {
    try {
      setScreenLoader(true);
      setSearchListLoading(true);
      setErrorMessage('');
      setMainSearch(true);
      const {
        firstName,
        lastName,
        dateOfBirth,
        ssn1,
        ssn2,
        ssn3,
        phoneNumber,
        agreementNumber,
        state,
        addressLine1,
        addressLine2,
        zip,
        city,
      } = customerBasicInformation;

      const limit = RECORD_CHECK_LIST_API_LIMIT;
      const offset = INITIAL_OFFSET;
      const storeNumber = getSelectedStore();
      setOffsetValue(offset);
      const payloadReq: Record<string, any> = {};

      const addIfExists = (key: string, value: any) => {
        if (value !== undefined && value !== null && value !== '') {
          payloadReq[key] = typeof value === 'string' ? value.trim() : value;
        }
      };

      addIfExists('firstName', firstName);
      addIfExists('lastName', lastName);
      addIfExists('dateOfBirth', dateOfBirth);
      addIfExists('agreementNumber', agreementNumber);
      addIfExists('addressLine1', addressLine1);
      addIfExists('addressLine2', addressLine2);
      addIfExists('zip', zip);
      addIfExists('city', city);

      if (state && state !== '0') {
        payloadReq.state = state;
      }

      if (ssn1 && ssn2 && ssn3) {
        payloadReq.ssn = `${ssn1}${ssn2}${ssn3}`;
      }

      if (phoneNumber) {
        const cleanedPhone = phoneNumber.replace(/\D/g, '');
        if (cleanedPhone.length === 10) {
          payloadReq.phoneNumber = cleanedPhone;
        }
      }

      if (queuedCustomers.length > 0 || selectedCustomerQueue.length > 0) {
        const excludeIds = [
          ...queuedCustomers.map((c) => c.customerId),
          ...selectedCustomerQueue.map((c) => c.customerId),
        ];

        payloadReq.excludeCustomerIds = Array.from(new Set(excludeIds));
      }

      const payload = {
        ...payloadReq,
        limit,
        offset,
        storeNumber,
      };
      console.log('payload', payload);

      const response = await postSearch(payload);
      const customers = response?.customerDetails ?? [];
      setMainSearch(customers.length === 0);
      setSearchResults(customers);
    } catch (error) {
      console.error('Search failed:', error);
      setErrorMessage('Search failed. Please try again.');
    } finally {
      setScreenLoader(false);
      setSearchListLoading(false);
      setSearchCompleted(true);
    }
  };

  return (
    <>
      <Grid container md={12}>
        <Typography
          className={`${classes.fontOpensansBold} ${classes.fontSize16px} ${classes.pm}`}
        >
          {t('Search Criteria')}
        </Typography>
      </Grid>
      <Card className="card">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item lg={2} md={4} sm={6}>
              <RACTextbox
                type="text"
                name="lastName"
                value={customerBasicInformation.lastName}
                inputlabel={t('Last Name')}
                maxlength={30}
                OnChange={(e: any) => handleInputChange(e)}
                isCurrency={false}
              />
            </Grid>
            <Grid item lg={2} md={4} sm={6}>
              <RACTextbox
                type="text"
                name="firstName"
                inputlabel={t('First Name')}
                value={customerBasicInformation.firstName}
                maxlength={30}
                OnChange={(e: any) => handleInputChange(e)}
                isCurrency={false}
              />
            </Grid>
            <Grid item md={4} lg={2} sm={6} className={classes.datePicker}>
              <Box
                component="div"
                className={`${classes.formLabel} ${classes.datePickerAddCo}`}
              >
                {t('Date of Birth')}
              </Box>

              {DateTimeLocalization ? (
                /* eslint-disable sonarjs/no-duplicate-string */
                <RACMUIDatePicker
                  id="a11y_dob"
                  label=""
                  value={customerBasicInformation.dateOfBirth}
                  name={'dateOfBirth'}
                  onChange={(e: any) => handleDateChange(e)}
                  inputProps={{
                    min: moment().subtract(110, 'years').format('YYYY-MM-DD'),
                    max: moment().subtract(18, 'years').format('YYYY-MM-DD'),
                  }}
                />
              ) : (
                /* eslint-disable sonarjs/no-duplicate-string */
                <RACDatePicker
                  id="a11y_dob"
                  label=""
                  value={customerBasicInformation.dateOfBirth}
                  name={'dateOfBirth'}
                  onChange={(e: any) => handleDateChange(e)}
                  inputProps={{
                    min: moment().subtract(110, 'years').format('YYYY-MM-DD'),
                    max: moment().subtract(18, 'years').format('YYYY-MM-DD'),
                  }}
                />
              )}
            </Grid>
            <Grid item lg={2} md={4} sm={6} className={classes.spacerPT1}>
              <label htmlFor="a11y_ssn" className={classes.formLabel}>
                {t('Social Security# / ITIN#')}
              </label>
              <Grid container spacing={2} className={classes.ssnpadding}>
                <Grid item md={4} sm={4} className={`${classes.ssnPaddingTop}`}>
                  <RACTextbox
                    type="password"
                    name="ssn1"
                    value={customerBasicInformation.ssn1}
                    placeHolder={'xxx'}
                    id={'a11y_ssnone'}
                    className={classes.textCenterSSN}
                    maxlength={3}
                    OnChange={ssn1Handler}
                    isCurrency={false}
                  />
                </Grid>
                <Grid item md={3} sm={3} className={`${classes.ssnPaddingTop}`}>
                  <RACTextbox
                    type="password"
                    name="ssn2"
                    value={customerBasicInformation.ssn2}
                    placeHolder={'xx'}
                    id={'a11y_ssntwo'}
                    className={classes.textCenterSSN}
                    formControlClassName={classes.ssnTextField}
                    maxlength={2}
                    OnChange={ssn2Handler}
                    isCurrency={false}
                  />
                </Grid>
                <Grid item md={5} sm={5} className={`${classes.ssnPaddingTop}`}>
                  <RACTextbox
                    type="text"
                    name="ssn3"
                    value={customerBasicInformation.ssn3}
                    placeHolder={'xxxx'}
                    id={'a11y_ssnthree'}
                    className={classes.textCenterSSN}
                    maxlength={4}
                    OnChange={ssn3Handler}
                    isCurrency={false}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item lg={2} md={4} sm={6}>
              <RACTextbox
                type="phoneno"
                maxlength={14}
                inputlabel={t('Phone Number')}
                id="a11y_phonenumber"
                name="phoneNumber"
                value={customerBasicInformation.phoneNumber}
                OnChange={(e) => handleInputChange(e)}
                isCurrency={false}
              />
            </Grid>
            <Grid item lg={2} md={4} sm={6}>
              <RACTextbox
                type="text"
                maxlength={19}
                inputlabel={t('Agreement #')}
                id="a11y_agreementumber"
                name="agreementNumber"
                value={customerBasicInformation.agreementNumber}
                OnChange={(e: any) => handleInputChange(e)}
                isCurrency={false}
              />
            </Grid>
          </Grid>
          <Grid container md={12} className={`${classes.pt4} `}>
            <Grid container spacing={2} md={4}>
              <Grid item sm={6} md={6}>
                <RACTextbox
                  type="text"
                  name="addressLine1"
                  placeHolder={t('Address Line 1')}
                  inputlabel={t('Address Line 1')}
                  maxlength={30}
                  value={customerBasicInformation.addressLine1}
                  inputLabelClassname={classes.mb1}
                  OnChange={(e) => handleInputChange(e)}
                />
              </Grid>
              <Grid item sm={6} md={6}>
                <RACTextbox
                  type="text"
                  name="addressLine2"
                  placeHolder={t('APT #, Unit #, P.O. Box')}
                  inputlabel={t('Apt/Unit #')}
                  value={customerBasicInformation.addressLine2}
                  inputLabelClassname={classes.mb1}
                  maxlength={30}
                  OnChange={(e) => handleInputChange(e)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className={`${classes.ms2}`} md={4}>
              <Grid item sm={4} md={4}>
                <RACTextbox
                  type="text"
                  name="zip"
                  inputlabel={t('Zip')}
                  placeHolder={t('Zip Code')}
                  maxlength={10}
                  minlength={5}
                  value={customerBasicInformation.zip}
                  inputLabelClassname={classes.mb1}
                  OnChange={(e) => {
                    let zipCode = ('' + e.target.value).replace(/\D/g, '');
                    zipCode =
                      zipCode.length > 5
                        ? zipCode.slice(0, 5) + '-' + zipCode.slice(5)
                        : zipCode;
                    e.target.name = 'zip';
                    e.target.value = zipCode;
                    handleInputChange(e);
                  }}
                />
              </Grid>

              <Grid item sm={4} md={4}>
                <RACTextbox
                  type="text"
                  name="city"
                  inputlabel={t('City')}
                  value={customerBasicInformation.city}
                  placeHolder={t('City')}
                  maxlength={30}
                  inputLabelClassname={classes.mb1}
                  OnChange={(e) => handleInputChange(e)}
                />
              </Grid>
              <Grid item sm={4} md={4}>
                <RACSelect
                  loading={dropDownLoading}
                  inputLabel={t('State')}
                  options={stateOptions}
                  inputLabelClassName={classes.mb1}
                  name="state"
                  defaultValue={customerBasicInformation.state}
                  onChange={(e) => {
                    e.target.name = `state`;
                    handleInputChange(e);
                  }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} className={`${classes.ms2}`} md={4}>
              <Grid item md={10} className={`${classes.mt2}`}>
                <RACButton
                  variant="contained"
                  color="primary"
                  onClick={() => handleSearch()}
                  disabled={isSearchButtonEnabled}
                  size="small"
                  className={`${classes.floatRight} ${classes.ms2} ${classes.mt2}`}
                >
                  {t('Search')}
                </RACButton>

                <RACButton
                  variant="outlined"
                  color="primary"
                  onClick={resetForm}
                  className={`${classes.floatRight} ${classes.p10p25} ${classes.mt2}`}
                  disabled={
                    customerBasicInformation.firstName.length == 0 &&
                    customerBasicInformation.lastName.length == 0 &&
                    customerBasicInformation.dateOfBirth == '' &&
                    customerBasicInformation.ssn1.length == 0 &&
                    customerBasicInformation.ssn2.length == 0 &&
                    customerBasicInformation.ssn3.length == 0 &&
                    customerBasicInformation.phoneNumber.length == 0 &&
                    customerBasicInformation.agreementNumber.length == 0 &&
                    customerBasicInformation.addressLine1.length == 0 &&
                    customerBasicInformation.addressLine2.length == 0 &&
                    customerBasicInformation.zip.length == 0 &&
                    customerBasicInformation.city.length == 0 &&
                    customerBasicInformation.state == '0'
                      ? true
                      : false
                  }
                >
                  {t('Clear')}
                </RACButton>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {errorMessage && (
        <Grid container className={`${classes.mt10px} ${classes.width100p}`}>
          <Typography className={classes.errorText}>{errorMessage}</Typography>
        </Grid>
      )}
      <Grid>
        {screenLoader === true ? (
          <Grid className={classes.masterLoader}>
            <Grid className={classes.Loader}>
              <CircularProgress />
            </Grid>
          </Grid>
        ) : null}
      </Grid>
      {searchCompleted && (
        <Grid container className={`${classes.mt10px} ${classes.width100p}`}>
          <Typography
            className={`${classes.fontOpensansBold}  ${classes.fontSize16px} ${classes.p10}`}
          >
            {t('Search Results')}
          </Typography>
          <CustomerSearchResultGrid />
        </Grid>
      )}
    </>
  );
}
